import hostGroteskLatin from "@fontsource-variable/host-grotesk/files/host-grotesk-latin-wght-normal.woff2?url";
import { loadAnalytics } from "@marketing/analytics";
import { MarketingFooter } from "@marketing/components/MarketingFooter";
import { MarketingHeader } from "@marketing/components/MarketingHeader";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { useEffect } from "react";

/**
 * The public site: always light, whatever theme an admin picked for the app. `.mk` resets the
 * base tokens and carries the site's palette (modules/marketing/marketing.css).
 */
export const Route = createFileRoute("/_marketing")({
	// The headline's font, fetched with the page instead of after the stylesheet asks for it.
	head: () => ({
		links: [
			{
				rel: "preload",
				href: hostGroteskLatin,
				as: "font",
				type: "font/woff2",
				crossOrigin: "anonymous",
			},
		],
	}),
	component: MarketingLayout,
});

function MarketingLayout() {
	// Analytics after the visitor's first scroll, tap or key, or after 10 s for someone who only
	// reads: never in the way of the page loading.
	useEffect(() => {
		const events = [
			"pointerdown",
			"keydown",
			"scroll",
			"touchstart",
		] as const;
		const start = () => {
			stop();
			void loadAnalytics();
		};
		const fallback = setTimeout(start, 10_000);
		const stop = () => {
			clearTimeout(fallback);
			for (const name of events) {
				window.removeEventListener(name, start);
			}
		};
		for (const name of events) {
			window.addEventListener(name, start, { once: true, passive: true });
		}
		return stop;
	}, []);

	return (
		<div className="mk flex min-h-dvh flex-col">
			<a
				href="#main"
				className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:rounded-full focus:bg-(--mk-ink) focus:px-4 focus:py-2 focus:text-white"
			>
				Skip to content
			</a>
			<MarketingHeader />
			<main id="main" className="flex-1">
				<Outlet />
			</main>
			<MarketingFooter />
		</div>
	);
}
